<template>
  <div class="ai-chat-container">
    <div class="chat-messages" ref="messagesContainer">
      <div v-for="(message, index) in messages" :key="index" class="message" :class="message.role">
        <div class="avatar" :class="message.role">
          {{ message.role === 'user' ? '我' : 'AI' }}
        </div>
        <div class="message-content">{{ message.content }}</div>
      </div>
      <div v-if="isStreaming" class="message assistant">
        <div class="avatar assistant">AI</div>
        <div class="message-content">{{ streamedContent }}<span class="typing-indicator">▌</span></div>
      </div>
      <div v-if="errorMessage" class="message system-error">
        <div class="avatar system">!</div>
        <div class="message-content error">
          {{ errorMessage }}
          <button class="retry-btn" @click="retryLastMessage">重试</button>
        </div>
      </div>
    </div>
    <div class="chat-footer">
      <div class="ai-disclaimer">内容由AI生成</div>
      <div class="chat-input-area">
        <input
          type="text"
          v-model="userInput"
          placeholder="请输入您的问题..."
          @keyup.enter="sendMessage"
          :disabled="isStreaming"
          ref="inputRef"
        />
        <button @click="sendMessage" :disabled="isStreaming || !userInput.trim()">
          <span v-if="isStreaming" class="send-icon">
            <span class="iconify" data-icon="hugeicons:loading-02"></span>
          </span>
          <span v-else>发送</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import config from '../config';

const API_BASE_URL = config.API_BASE_URL;

export default {
  name: 'AIChat',
  data() {
    return {
      messages: [],
      userInput: '',
      isStreaming: false,
      streamedContent: '',
      errorMessage: '',
      lastUserMessage: ''
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.inputRef?.focus();
    });
  },
  watch: {
    messages: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    isStreaming(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    }
  },
  methods: {
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    },

    async sendMessage() {
      if (!this.userInput.trim() || this.isStreaming) return;

      const userMessage = this.userInput.trim();
      this.lastUserMessage = userMessage;
      this.messages.push({ role: 'user', content: userMessage });
      this.userInput = '';
      this.errorMessage = '';
      this.isStreaming = true;
      this.streamedContent = '';

      await this.callApiWithErrorHandling(userMessage);
    },

    async retryLastMessage() {
      if (!this.lastUserMessage || this.isStreaming) return;
      this.errorMessage = '';
      this.isStreaming = true;
      await this.callApiWithErrorHandling(this.lastUserMessage);
    },

    async callApiWithErrorHandling(userMessage) {
      try {
        await this.callLocalApi(userMessage);
      } catch (error) {
        console.error('API调用失败:', error);
        this.isStreaming = false;
        this.streamedContent = '';
        this.errorMessage = '哎呀，您把智小家问倒啦，请联系我们的智慧家庭工程师帮助您。';
      }
    },
    
    async callLocalApi(userMessage) {
      try {
        // 构建包含完整上下文的文本
        let fullText = '';
        
        // 添加之前的对话历史
        this.messages.forEach(msg => {
          const rolePrefix = msg.role === 'user' ? '用户: ' : 'AI: ';
          fullText += `${rolePrefix}${msg.content}\n`;
        });
        
        // 添加当前用户消息
        fullText += `用户: ${userMessage}`;
        
        // 调用本地API
        const response = await fetch(`${API_BASE_URL}/api/open/ai/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: fullText
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // 获取完整的JSON响应
        const apiResponse = await response.json();
        
        // 解析响应数据
        if (apiResponse.code === '200') {
          // API调用成功，使用data.text作为回复内容
          const aiResponse = apiResponse.data.text;
          this.messages.push({ role: 'assistant', content: aiResponse });
        } else {
          // API调用失败，使用message字段或默认错误信息
          const errorMessage = apiResponse.message || '哎呀，您把智小屋问倒啦，请联系我们的智慧家庭工程师帮助您。';
          this.messages.push({ role: 'assistant', content: errorMessage });
        }
        
        this.isStreaming = false;
        this.streamedContent = '';
      } catch (error) {
        console.error('调用本地API失败:', error);
        this.isStreaming = false;
        this.messages.push({ 
          role: 'assistant', 
          content: '哎呀，您把智小屋问倒啦，请联系我们的智慧家庭工程师帮助您。' 
        });
      }
    }
  }
};
</script>

<style scoped>
.ai-chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: transparent;
  border-radius: 20px;
  padding: 0;
  margin: 0;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 20px;
}

/* Message container styles */
.message {
  max-width: 80%;
  padding: 0;
  border-radius: 20px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.message.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message.assistant {
  align-self: flex-start;
}

/* Avatar styles */
.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: medium;
  flex-shrink: 0;
  margin-top: 4px;
}

.avatar.user {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.avatar.assistant {
  background-color: rgba(96, 165, 250, 0.8);
  color: white;
}

/* Message content styles */
.message-content {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 12px 16px;
  color: white;
  font-size: 14px;
  line-height: 1.6;
  max-width: calc(100% - 44px);
}

.message.user .message-content {
  background: rgba(96, 165, 250, 0.2);
  border-color: rgba(96, 165, 250, 0.3);
}

.message.system-error {
  align-self: flex-start;
}

.message-content.error {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.retry-btn {
  align-self: flex-start;
  padding: 6px 16px;
  background: rgba(239, 68, 68, 0.3);
  border: 1px solid rgba(239, 68, 68, 0.5);
  border-radius: 12px;
  color: white;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-btn:hover {
  background: rgba(239, 68, 68, 0.5);
}

.avatar.system {
  background-color: rgba(239, 68, 68, 0.8);
  color: white;
}

.send-icon .iconify {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Footer styles */
.chat-footer {
  flex-shrink: 0;
  padding-top: 8px;
}

.ai-disclaimer {
  text-align: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 8px;
}

/* Input area styles */
.chat-input-area {
  display: flex;
  gap: 12px;
  padding: 0;
}

input {
  flex: 1;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 14px 20px;
  color: white;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
}

input:focus {
  border-color: rgba(96, 165, 250, 0.5);
  background: rgba(255, 255, 255, 0.1);
}

input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

button {
  background: rgba(96, 165, 250, 0.8);
  backdrop-filter: blur(20px);
  border: none;
  border-radius: 24px;
  padding: 14px 28px;
  color: white;
  font-size: 14px;
  font-weight: medium;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

button:hover:not(:disabled) {
  background: rgba(96, 165, 250, 1);
  transform: scale(1.02);
}

button:active:not(:disabled) {
  transform: scale(0.98);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Typing indicator */
.typing-indicator {
  animation: blink 1s infinite;
  margin-left: 6px;
  font-size: 18px;
  vertical-align: middle;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

/* Scrollbar styling (hidden but functional) */
.chat-messages::-webkit-scrollbar {
  width: 4px;
}

.chat-messages::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>